/**
 * Schritt 0: Info und FAQ zum 3-Topf-Modell
 * Umfassende Informationsseite mit Beschreibung des 3-Topf vs. 1-Topf Portfolio Monte-Carlo Simulation Systems,
 * wissenschaftlichen Grundlagen und praktischen Anleitungen
 */

import React from 'react';
import {
  Alert,
  Box,
  ColumnLayout,
  Container,
  ExpandableSection,
  Header,
  Link,
  SpaceBetween,
  TextContent,
} from '@cloudscape-design/components';

/**
 * Komponente für umfassende Informationen und FAQ
 */
const Schritt0: React.FC = () => {
  // Grundlagen FAQ
  const grundlagenFAQ = [
    {
      id: 'was-ist-portfolio-3topf',
      question: 'Was ist die 3-Topf vs. 1-Topf Portfolio Monte-Carlo Simulation?',
      answer:
        'Die 3-Topf vs. 1-Topf Portfolio Monte-Carlo Simulation ist ein Finanzplanungs-Tool, das verschiedene Entnahmestrategien in der Ruhestandsphase vergleicht. Es simuliert sowohl die bewährte "Bucket Strategy" (3-Topf) als auch eine einfache Einzelportfolio-Strategie (1-Topf) für den deutschen Markt. Das Tool berücksichtigt historische Marktdaten und moderne Volatilitätsmodelle (GARCH) für realistische Szenarien.',
    },
    {
      id: 'bucket-strategy-grundlagen',
      question: 'Was ist die Bucket Strategy (3-Topf-Strategie)?',
      answer:
        'Die Bucket Strategy teilt das Vermögen in drei zeitbasierte "Töpfe" auf: 1) Verbrauchstopf (1 Jahr Ausgaben) für sofortige Verfügbarkeit, 2) Zinstopf (2-5 Jahre Ausgaben) für mittelfristige Stabilität, 3) Investitionstopf (Restkapital) für langfristiges Wachstum. Diese Strategie wird von vielen Finanzexperten empfohlen und ist besonders in der Entnahmephase bewährt.',
    },
    {
      id: 'wissenschaftliche-basis',
      question: 'Welche wissenschaftlichen Grundlagen verwendet die 3-Topf vs. 1-Topf Portfolio Monte-Carlo Simulation?',
      answer:
        'Die 3-Topf vs. 1-Topf Portfolio Monte-Carlo Simulation basiert auf modernen Portfoliotheorien und verwendet: 1) Historische Backtests mit echten Marktdaten seit 1970, 2) Monte-Carlo-Simulationen für Wahrscheinlichkeitsanalysen, 3) EGARCH-Modelle für realistische Volatilitätssimulation, 4) Verhaltensfinanz-Aspekte zur Krisenresistenz, 5) Wissenschaftlich fundierte Entnahmestrategien.',
    },
  ];

  // Anwendung FAQ
  const anwendungFAQ = [
    {
      id: 'wie-tool-verwenden',
      question: 'Wie verwende ich dieses Tool richtig?',
      answer:
        'Schritt 1: Geben Sie Ihre individuellen Parameter ein. Schritt 2: Wählen Sie Simulationsparameter (Inflation, Renditemodelle). Schritt 3: Führen Sie Einzelsimulationen durch für detaillierte Analysen. Schritt 4: Nutzen Sie Monte-Carlo-Simulation für Wahrscheinlichkeitsverteilungen.',
    },
    {
      id: 'parameter-bedeutung',
      question: 'Was bedeuten die verschiedenen Parameter?',
      answer:
        'Jährlicher Kapitalbedarf: Ihre geplanten Ausgaben pro Jahr (brutto, vor Steuern). Anzahl Jahre Topf 2: Wie viele Jahre soll der Zinstopf überbrücken (Standard: 3 Jahre). Entnahmerate: Prozentsatz des Portfolios, der jährlich entnommen wird. Rendite-Optionen: Verschiedene Modelle von konservativ (Parameter-Rendite) bis realistisch (historische/GARCH-Modelle).',
    },
    {
      id: 'ergebnisse-interpretieren',
      question: 'Wie interpretiere ich die Simulationsergebnisse?',
      answer:
        'Gesamtsaldo: Entwicklung des Gesamtvermögens über Zeit. Topf-Verteilung: Wie sich das Vermögen auf die drei Töpfe aufteilt. Entnahmen: Jährliche Entnahmen und deren steuerliche Behandlung. Krisenresistenz: Wie lange das Portfolio verschiedene Marktkrisen übersteht. Monte-Carlo: Wahrscheinlichkeitsverteilungen für verschiedene Szenarien.',
    },
  ];

  // Praktische FAQ
  const praktischeFAQ = [
    {
      id: 'entnahmeraten',
      question: 'Welche Entnahmeraten sind sinnvoll?',
      answer:
        '3-4%: Sehr konservativ, Vermögen bleibt real erhalten. 4-5%: Ausgewogen, leichter Realwertverlust über Jahrzehnte. 5-6%: Moderat aggressiv, kontrollierter Kapitalverzehr. 6%+: Nur bei hoher Risikotoleranz und ausreichend Vermögen. Das Tool zeigt Ihnen die Konsequenzen verschiedener Raten.',
    },
    {
      id: 'krisensicherheit',
      question: 'Wie krisensicher ist die 3-Topf-Strategie?',
      answer:
        'Die Strategie ist speziell für Krisen konzipiert: Der Zinstopf hält im Normalfall so viele Jahresentnahmen wie unter "Anzahl Jahre Topf 2" eingestellt (Standard: 3 Jahre) und wird in guten Jahren wieder auf dieses Ziel aufgefüllt. Da die Krisenerkennung dem Marktstatus des Vorjahres folgt, kommt real ein weiteres Jahr Verzögerung hinzu - der tatsächliche Schutz beträgt also rund "Anzahl Jahre Topf 2" + 1 Jahr (Standard: 4 Jahre). Reicht eine Krise darüber hinaus, müssen im Folgejahr Aktien aus dem Investitionstopf zu ungünstigen Kursen verkauft werden. Das Tool simuliert verschiedene Marktszenarien und Stress-Tests für Ihre Strategie.',
    },
    {
      id: 'portfolio-vergleich',
      question: 'Wie hilft der Vergleich zwischen 3-Topf und 1-Topf?',
      answer:
        'Das Tool vergleicht die krisenresistente 3-Topf-Strategie mit einer einfachen 1-Topf-Lösung (nur Aktien-ETF). Sie sehen direkt die Vorteile der Diversifikation: bessere Krisenresistenz, stabilere Entnahmen und reduziertes Sequence-of-Returns-Risiko, besonders in den ersten Jahren der Entnahmephase.',
    },
  ];

  // Video-Ressourcen
  const videoRessourcen = [
    {
      title: 'Leitfaden zur rationalen Finanzplanung - Autor: Dr. Andreas Beck',
      beschreibung:
        'Umfassender Leitfaden zur wissenschaftlich fundierten Finanzplanung. Dr. Beck erklärt die theoretischen Grundlagen der zeitbasierten Portfolioaufteilung und warum traditionelle Anlageberatung oft fehlerhaft ist.',
      url: 'https://www.youtube.com/watch?v=gSdrTbUBHMc',
      themen: 'Risikoverständnis, Anlagehorizonte, wissenschaftliche Portfoliotheorie',
    },
    {
      title: 'Bucket Strategy for Retirement - Pros & Cons - Autor: Joe Kuhn',
      beschreibung:
        'Detaillierte Erklärung der Bucket Strategy mit praktischen Vor- und Nachteilen. Zeigt verschiedene Implementierungsansätze und deren Auswirkungen auf die Portfolioperformance.',
      url: 'https://www.youtube.com/watch?v=BX1X2zq_9LI&t=730s',
      themen: 'Bucket Strategy, Entnahmestrategien, Portfolioaufteilung',
    },
    {
      title: 'How I Fill My 3 Buckets of Retirement Money - Autor: Joe Kuhn',
      beschreibung:
        'Praktische Umsetzung der 3-Bucket-Strategie mit konkreten Beispielen. Zeigt, wie die Töpfe befüllt und in verschiedenen Marktphasen verwaltet werden.',
      url: 'https://www.youtube.com/watch?v=vKMGjb1Myv0',
      themen: 'Praktische Umsetzung, Rebalancing, Marktphasen-Management',
    },
  ];

  // Tool-Features
  const toolFeatures = [
    {
      icon: '📊',
      title: 'Präzise Simulation Engine',
      description:
        'Hochentwickelte Simulationsalgorithmen mit historischen Daten ab 1970, Monte-Carlo-Verfahren und EGARCH-Volatilitätsmodellen für realistische Marktszenarien.',
    },
    {
      icon: '🛡️',
      title: '100% Datenschutz',
      description:
        'Alle Berechnungen erfolgen lokal in Ihrem Browser. Keine Datenübertragung, keine Server, keine Speicherung Ihrer sensiblen Finanzdaten.',
    },
    {
      icon: '📈',
      title: 'Erweiterte Analysen',
      description:
        'Von einfachen Einzelsimulationen bis zu komplexen Wahrscheinlichkeitsanalysen und GARCH-Modellierung.',
    },
    {
      icon: '🎯',
      title: 'Krisenresistenz-Tests',
      description:
        'Simulation verschiedener Marktszenarien und Krisenperioden zur Bewertung der Robustheit Ihrer Entnahmestrategie.',
    },
    {
      icon: '💾',
      title: 'Export & Dokumentation',
      description:
        'Vollständige Exportfunktionen für Simulationsergebnisse und Analysen zur weiteren Verwendung oder Beratung.',
    },
  ];

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Willkommen-Sektion */}
      <Container
        header={
          <Header
            variant="h1"
          >
            3-Topf vs. 1-Topf Portfolio Monte-Carlo Simulation
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <Alert type="info" dismissAriaLabel="Schließen" header="Was ist die 3-Topf vs. 1-Topf Portfolio Monte-Carlo Simulation?">
            Die 3-Topf vs. 1-Topf Portfolio Monte-Carlo Simulation ist ein Finanzplanungs-Tool, das verschiedene Entnahmestrategien vergleicht. Es simuliert sowohl die bewährte Bucket Strategy (3-Topf) als auch eine einfache Einzelportfolio-Strategie (1-Topf) und berücksichtigt historische Marktkrisen und
            moderne Volatilitätsmodelle.
          </Alert>


          {/* Tool Features Grid */}
          <ColumnLayout columns={3} borders="vertical">
            {toolFeatures.map((feature, index) => (
              <div
                key={index}
                style={{
                  padding: '16px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>{feature.icon}</div>
                <Box variant="h4" margin={{ bottom: 's' }}>
                  {feature.title}
                </Box>
                <Box variant="p" color="text-body-secondary">
                  {feature.description}
                </Box>
              </div>
            ))}
          </ColumnLayout>
        </SpaceBetween>
      </Container>

      {/* Video-Ressourcen */}
      <Container
        header={<Header variant="h2">📺 Empfohlene Video-Ressourcen zur Bucket Strategy</Header>}
      >
        <SpaceBetween direction="vertical" size="m">
          <Alert type="success" statusIconAriaLabel="Info">
            Diese Videos erklären die theoretischen und praktischen Grundlagen der 3-Topf-Strategie,
            die in diesem Tool implementiert ist. Schauen Sie sich diese an, um die Strategie besser
            zu verstehen.
          </Alert>

          <ColumnLayout columns={1}>
            {videoRessourcen.map((video, index) => (
              <div
                key={index}
                style={{
                  padding: '16px',
                  border: '1px solid #d5dbdb',
                  borderRadius: '8px',
                  backgroundColor: '#fafbfc',
                }}
              >
                <Box variant="h3" margin={{ bottom: 's' }}>
                  🎥 {video.title}
                </Box>
                <Box variant="p" margin={{ bottom: 's' }}>
                  {video.beschreibung}
                </Box>
                <Box variant="p" margin={{ bottom: 's' }} color="text-label">
                  <strong>Themen:</strong> {video.themen}
                </Box>
                <Link external href={video.url} fontSize="body-m">
                  Video auf YouTube ansehen →
                </Link>
              </div>
            ))}
          </ColumnLayout>
        </SpaceBetween>
      </Container>

      {/* Grundlagen FAQ */}
      <Container header={<Header variant="h2">📖 Grundlagen & Theorie</Header>}>
        <SpaceBetween direction="vertical" size="s">
          {grundlagenFAQ.map(item => (
            <ExpandableSection
              key={item.id}
              headerText={item.question}
              variant="container"
              defaultExpanded={false}
            >
              <TextContent>
                <p>{item.answer}</p>
              </TextContent>
            </ExpandableSection>
          ))}
        </SpaceBetween>
      </Container>

      {/* Anwendung FAQ */}
      <Container header={<Header variant="h2">🛠️ Tool-Anwendung & Bedienung</Header>}>
        <SpaceBetween direction="vertical" size="s">
          {anwendungFAQ.map(item => (
            <ExpandableSection
              key={item.id}
              headerText={item.question}
              variant="container"
              defaultExpanded={false}
            >
              <TextContent>
                <p>{item.answer}</p>
              </TextContent>
            </ExpandableSection>
          ))}
        </SpaceBetween>
      </Container>

      {/* Praktische FAQ */}
      <Container header={<Header variant="h2">💡 Praktische Anwendung & Strategien</Header>}>
        <SpaceBetween direction="vertical" size="s">
          {praktischeFAQ.map(item => (
            <ExpandableSection
              key={item.id}
              headerText={item.question}
              variant="container"
              defaultExpanded={false}
            >
              <TextContent>
                <p>{item.answer}</p>
              </TextContent>
            </ExpandableSection>
          ))}
        </SpaceBetween>
      </Container>

      {/* Funktionsweise Schritt-für-Schritt */}
      <Container header={<Header variant="h2">🚀 So verwenden Sie die 3-Topf vs. 1-Topf Portfolio Monte-Carlo Simulation</Header>}>
        <SpaceBetween direction="vertical" size="m">
          <Alert type="info" statusIconAriaLabel="Info">
            Folgen Sie diesen Schritten für optimale Ergebnisse:
          </Alert>

          <ColumnLayout columns={2} borders="vertical">
            <SpaceBetween direction="vertical" size="m">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div
                  style={{
                    minWidth: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#0972d3',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    flexShrink: 0,
                  }}
                >
                  1
                </div>
                <div>
                  <Box variant="h4" margin={{ bottom: 'xs' }}>
                    Schritt 1: Individuelle-Parameter eingeben
                  </Box>
                  <Box variant="p" color="text-body-secondary">
                    Konfigurieren Sie Ihre individuellen Daten: Alter bei Rentenbeginn, Berechnung bis Lebensalter, Vorhandenes Kapital, Anzahl Jahre Topf 2. Diese bilden die Basis aller Berechnungen.
                  </Box>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div
                  style={{
                    minWidth: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#0972d3',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    flexShrink: 0,
                  }}
                >
                  2
                </div>
                <div>
                  <Box variant="h4" margin={{ bottom: 'xs' }}>
                    Schritt 2: Simulations-Parameter
                  </Box>
                  <Box variant="p" color="text-body-secondary">
                    Wählen Sie Renditemodelle für die drei Töpfe. Von konservativen
                    Parameter-Renditen bis zu realistischen GARCH-Modellen.
                  </Box>
                </div>
              </div>
            </SpaceBetween>

            <SpaceBetween direction="vertical" size="m">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div
                  style={{
                    minWidth: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#0972d3',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    flexShrink: 0,
                  }}
                >
                  3
                </div>
                <div>
                  <Box variant="h4" margin={{ bottom: 'xs' }}>
                    Schritt 3: Einzel-Simulation
                  </Box>
                  <Box variant="p" color="text-body-secondary">
                    Führen Sie detaillierte Simulationen durch. Vergleichen Sie 3-Topf vs. 1-Topf
                    Strategien und analysieren Sie die Entwicklung.
                  </Box>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div
                  style={{
                    minWidth: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#0972d3',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    flexShrink: 0,
                  }}
                >
                  4
                </div>
                <div>
                  <Box variant="h4" margin={{ bottom: 'xs' }}>
                    Schritt 4: Monte-Carlo-Simulation
                  </Box>
                  <Box variant="p" color="text-body-secondary">
                    Nutzen Sie Wahrscheinlichkeitsanalysen für robuste Ergebnisse. 1000+
                    Simulationen zeigen verschiedene Marktszenarien.
                  </Box>
                </div>
              </div>
            </SpaceBetween>
          </ColumnLayout>

          <Alert type="success" statusIconAriaLabel="Tipp">
            <strong>Tipp:</strong> Beginnen Sie mit konservativen Parametern und arbeiten Sie
            sich zu realistischeren Modellen vor. So verstehen Sie die Auswirkungen verschiedener
            Annahmen.
          </Alert>
        </SpaceBetween>
      </Container>

      {/* Rechtliche Hinweise */}
      <Container header={<Header variant="h2">⚖️ Rechtliche Hinweise & Haftungsausschluss</Header>}>
        <SpaceBetween direction="vertical" size="m">
          <Alert type="warning" dismissAriaLabel="Schließen" header="Wichtiger rechtlicher Hinweis">
            Dieses Tool dient ausschließlich zur allgemeinen, unverbindlichen Information und
            Bildung. Es ersetzt keine individuelle Finanz- oder Steuerberatung durch qualifizierte
            Berater.
          </Alert>

          <ExpandableSection
            headerText="Vollständiger Haftungsausschluss"
            variant="container"
            defaultExpanded={false}
          >
            <TextContent>
              <ul>
                <li>
                  <strong>Keine Anlageberatung:</strong> Die 3-Topf vs. 1-Topf Portfolio Monte-Carlo Simulation stellt keine
                  Anlageberatung, Finanzanalyse oder Empfehlung zum Kauf/Verkauf von Wertpapieren
                  dar.
                </li>
                <li>
                  <strong>Simulationen sind Schätzungen:</strong> Alle Ergebnisse basieren auf
                  historischen Daten und Modellen. Zukünftige Renditen können abweichen.
                </li>
                <li>
                  <strong>Eigenverantwortung:</strong> Alle Anlageentscheidungen treffen Sie auf
                  eigene Verantwortung nach sorgfältiger Prüfung Ihrer Situation.
                </li>
                <li>
                  <strong>Kein Datenschutz-Risiko:</strong> Da alle Berechnungen lokal erfolgen,
                  entstehen keine Datenschutz-Risiken durch Datenübertragung.
                </li>
              </ul>
            </TextContent>
          </ExpandableSection>
        </SpaceBetween>
      </Container>

    </SpaceBetween>
  );
};

export default Schritt0;
